import { useAuth } from "AuthProvider";
import { getAuth, onAuthStateChanged, signOut } from "firebase/auth";
import useFetch from "hooks/useFetch";
import { LEAVE_WITHOUT_ASKING } from "hooks/useLeaveGuard";
import React, { useEffect, useRef, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import Select, {
  createFilter,
  SelectInstance,
  StylesConfig,
} from "react-select";
import { Icon, styles as ui, useToast } from "stories/Admin";
import { ReactComponent as DefaultLogo } from "../../assets/default-logo.svg";
import { getIdToken } from "../../firebase";
import mawaqeetLogo from "../../assets/ramadan/mawaqeet-logo.png";
import styles from "./AdminLayout.module.scss";
import {
  AdminRole,
  formatOrgNumber,
  isItemActive,
  menuGroupsFor,
  roleBadge,
} from "./menuConfig";
import {
  countFor,
  InquirySummary,
  onInquiryRefresh,
  setInquirySummary,
  SUMMARY_URL,
  useInquirySummary,
} from "pages/SuperAdminPages/InquiriesPage/inquiries";

interface Mosque {
  orgNr: string;
  orgName: string;
  name?: string;
  logo?: string;
}

interface MosqueOption {
  value: string;
  label: string;
  orgName: string;
  name?: string;
  logo?: string;
}

const switcherStyles: StylesConfig<MosqueOption, false> = {
  control: (base, state) => ({
    ...base,
    minHeight: 38,
    borderRadius: 11,
    background: "rgba(255,255,255,0.08)",
    borderColor: state.isFocused ? "#8FE0B6" : "rgba(255,255,255,0.18)",
    boxShadow: "none",
    "&:hover": { borderColor: "rgba(255,255,255,0.3)" },
  }),
  input: (base) => ({ ...base, color: "#FFFFFF" }),
  singleValue: (base) => ({ ...base, color: "#FFFFFF" }),
  placeholder: (base) => ({ ...base, color: "rgba(255,255,255,0.55)" }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (base) => ({ ...base, color: "rgba(255,255,255,0.6)" }),
  menuPortal: (base) => ({ ...base, zIndex: 1200 }),
  menu: (base) => ({
    ...base,
    borderRadius: 12,
    overflow: "hidden",
    fontSize: 13,
    color: "#0D2A33",
  }),
  option: (base, state) => ({
    ...base,
    background: state.isSelected
      ? "#0B3B4C"
      : state.isFocused
        ? "#F1FAF5"
        : "#FFFFFF",
    color: state.isSelected ? "#FFFFFF" : "#0D2A33",
  }),
};

const roleFor = (
  isSuperAdmin: boolean | null,
  isMawaqeetAdmin: boolean | null,
  superAdminMode: boolean,
): AdminRole => {
  if (superAdminMode && isSuperAdmin) return "superadmin";
  if (isMawaqeetAdmin) return "mawaqeet";
  if (isSuperAdmin) return "super-moske";
  return "moske";
};

/** Side menu (AdmMeny) and page frame for all admin roles. */
const AdminLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { execute } = useFetch<Mosque[]>();
  const { execute: fetchSummary } = useFetch<InquirySummary>();
  const fetchSummaryRef = useRef(fetchSummary);
  fetchSummaryRef.current = fetchSummary;
  const inquirySummary = useInquirySummary();
  const {
    setOrgNumber,
    setUid,
    mosqueName,
    selectedOrgName,
    mosqueLogo,
    setMosqueLogo,
    setMosqueName,
    isSuperAdmin,
    isMawaqeetAdmin,
    superAdminMode,
    setSuperAdminMode,
    selectedOrgNumber,
    setSelectedOrgNumber,
    setSelectedOrgName,
  } = useAuth();

  const role = roleFor(isSuperAdmin, isMawaqeetAdmin, superAdminMode);
  const groups = menuGroupsFor(role);
  const badge = roleBadge[role];

  const [mosqueList, setMosqueList] = useState<Mosque[]>([]);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [email, setEmail] = useState<string | null>(null);
  const [navOpen, setNavOpen] = useState(false);
  const selectRef = useRef<SelectInstance<MosqueOption, false>>(null);

  useEffect(() => {
    try {
      return onAuthStateChanged(getAuth(), (user) =>
        setEmail(user?.email ?? null),
      );
    } catch {
      return undefined;
    }
  }, []);

  // Superadmins can switch to any mosque; a mosque admin only between the mosques they administer.
  useEffect(() => {
    const fetchMosques = async () => {
      if (role === "super-moske") {
        const result = await execute(`/api/v1/mosques`, "GET");
        if (result) {
          const sortedMosques = result.sort((a: Mosque, b: Mosque) =>
            a.orgName.localeCompare(b.orgName),
          );
          setMosqueList(sortedMosques);
        }
      } else if (role === "moske") {
        const result = (await execute(
          `/api/v1/me/admin-mosques`,
          "GET",
        )) as unknown as { mosques?: Mosque[] } | null;
        const mine = result?.mosques ?? [];
        setMosqueList(mine);
        restoreLastMosque(mine);
      }
    };
    fetchMosques();
  }, [role, selectedOrgNumber]);

  // After login, open the mosque the admin last switched to (if they still administer it).
  const restoreLastMosque = (mine: Mosque[]) => {
    if (sessionStorage.getItem("adminMosqueRestored")) return;
    sessionStorage.setItem("adminMosqueRestored", "true");
    let last: string | null = null;
    try {
      last = localStorage.getItem("lastAdminOrgNumber");
    } catch {
      return;
    }
    const mosque = mine.find((m) => m.orgNr === last);
    if (!mosque || mosque.orgNr === selectedOrgNumber) return;
    setMosqueName("");
    setSelectedOrgNumber(mosque.orgNr);
    sessionStorage.setItem("selectedOrgNumber", mosque.orgNr);
    setSelectedOrgName(mosque.orgName);
    sessionStorage.setItem("selectedOrgName", mosque.orgName);
    setMosqueLogo(mosque.logo ?? "");
    sessionStorage.setItem("mosqueLogo", mosque.logo ?? "");
  };

  const canSwitch =
    role === "super-moske" || (role === "moske" && mosqueList.length > 1);

  // ⌘K / Ctrl+K opens «Bytt moské».
  useEffect(() => {
    if (!canSwitch) return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSwitcherOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [canSwitch]);

  useEffect(() => {
    if (switcherOpen) selectRef.current?.focus();
  }, [switcherOpen]);

  useEffect(() => setNavOpen(false), [location.pathname]);

  // Superadmin: tellerne for Henvendelser. Hentes ved sidebytte, hvert minutt og når en side ber om det.
  useEffect(() => {
    if (role !== "superadmin") return;
    let active = true;
    const load = async () => {
      const result = await fetchSummaryRef.current(SUMMARY_URL, "GET");
      if (active && result) setInquirySummary(result);
    };
    load();
    const timer = window.setInterval(load, 60_000);
    const off = onInquiryRefresh(load);
    return () => {
      active = false;
      window.clearInterval(timer);
      off();
    };
  }, [role, location.pathname]);

  const handleLogout = async () => {
    const auth = getAuth();
    try {
      await signOut(auth);

      sessionStorage.clear();
      setOrgNumber("");
      setUid("");
      navigate("/innlogging");
    } catch (error) {
      console.error("Failed to log out:", error);
      toast.error("Noe gikk galt under utloggingen. Prøv igjen senere.");
    }
  };

  const handleGoToSuperAdmin = () => {
    sessionStorage.removeItem("mosqueName");
    sessionStorage.removeItem("mosqueLogo");
    sessionStorage.removeItem("prayerTimeTableId");
    sessionStorage.removeItem("selectedOrgNumber");

    navigate("/velg-moske", { state: LEAVE_WITHOUT_ASKING });
  };

  const handleManageMosque = () => {
    setSuperAdminMode(false);
    sessionStorage.removeItem("isSuperAdminMode");
    navigate("/velg-moske", { state: LEAVE_WITHOUT_ASKING });
  };

  const handleCopyToken = async () => {
    const token = await getIdToken();
    if (token) {
      navigator.clipboard.writeText(token).then(
        () => toast.success("Token er kopiert til utklippstavlen."),
        (err) => {
          console.error("Kunne ikke kopiere token:", err);
          toast.error("Kunne ikke kopiere token.");
        },
      );
    } else {
      toast.error("Fant ingen token. Logg inn på nytt.");
    }
  };

  const handleMosqueSelect = (selectedOption: MosqueOption | null) => {
    setMosqueLogo("");
    setMosqueName("");

    if (selectedOption) {
      const selectedOrgNr = selectedOption.value;

      setSelectedOrgNumber(selectedOrgNr);
      sessionStorage.setItem("selectedOrgNumber", selectedOrgNr);

      setSelectedOrgName(selectedOption.orgName);
      sessionStorage.setItem("selectedOrgName", selectedOption.orgName);

      setMosqueLogo(selectedOption.logo as string);
      sessionStorage.setItem("mosqueLogo", selectedOption.logo as string);

      if (role === "moske") {
        try {
          localStorage.setItem("lastAdminOrgNumber", selectedOrgNr);
        } catch {
          // Private mode: the choice is just not remembered.
        }
      }

      setSwitcherOpen(false);
      // Moskeen er allerede byttet, så ikke spør om ulagrede endringer.
      navigate("/", { state: LEAVE_WITHOUT_ASKING });
    }
  };

  const mosqueOptions: MosqueOption[] = mosqueList.map((mosque) => ({
    value: mosque.orgNr,
    label: (mosque.name || mosque.orgName)?.toUpperCase(),
    orgName: mosque.orgName,
    name: mosque.name,
    logo: mosque.logo,
  }));

  const customFilter = createFilter<MosqueOption>({
    ignoreCase: true,
    ignoreAccents: true,
    matchFrom: "any",
    stringify: (option) => `${option.data.orgName} ${option.data.name}`,
  });

  const displayName = mosqueName || selectedOrgName;
  const homePath =
    role === "superadmin"
      ? "/moske-admin"
      : role === "mawaqeet"
        ? "/mawaqeet-bonnetider"
        : "/";

  return (
    <div className={`${ui.root} ${styles.shell}`}>
      <button
        type="button"
        className={styles.navToggle}
        aria-expanded={navOpen}
        aria-controls="adm-nav"
        onClick={() => setNavOpen((open) => !open)}
      >
        <Icon name="table" size={18} />
        Meny
      </button>

      <nav
        id="adm-nav"
        aria-label="Hovedmeny"
        className={`${styles.nav} ${navOpen ? styles.navOpen : ""}`}
      >
        <Link to={homePath} className={styles.brand}>
          <span className={styles.brandMark}>
            <Icon name="moon" size={19} strokeWidth={2.2} />
          </span>
          <span className={styles.brandName}>iBønn Admin</span>
          <span className={styles.roleBadge} style={{ background: badge.tone }}>
            {badge.label}
          </span>
        </Link>

        {(role === "moske" || role === "super-moske") && (
          <div className={styles.ctx}>
            <span className={styles.ctxRow}>
              <span className={styles.ctxLogo}>
                {mosqueLogo ? (
                  <img src={mosqueLogo} alt="" />
                ) : (
                  <DefaultLogo aria-hidden="true" />
                )}
              </span>
              <span className={styles.ctxText}>
                <span className={styles.ctxName} title={displayName ?? ""}>
                  {displayName || "Moské"}
                </span>
                {selectedOrgNumber && (
                  <span className={styles.ctxSub}>
                    Org.nr {formatOrgNumber(selectedOrgNumber)}
                  </span>
                )}
              </span>
            </span>
            {canSwitch &&
              (switcherOpen ? (
                <Select<MosqueOption, false>
                  ref={selectRef}
                  options={mosqueOptions}
                  onChange={handleMosqueSelect}
                  placeholder="Søk og velg moské"
                  filterOption={customFilter}
                  value={mosqueOptions.find(
                    (option) => option.value === selectedOrgNumber,
                  )}
                  noOptionsMessage={() => "Fant ingen moskeer"}
                  aria-label="Bytt moské"
                  styles={switcherStyles}
                  menuPortalTarget={document.body}
                  defaultMenuIsOpen
                  openMenuOnFocus
                  onBlur={() => setSwitcherOpen(false)}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") setSwitcherOpen(false);
                  }}
                />
              ) : (
                <button
                  type="button"
                  className={styles.switchBtn}
                  onClick={() => setSwitcherOpen(true)}
                >
                  <Icon name="swap" size={16} />
                  Bytt moské
                  <span className={styles.kbd}>⌘K</span>
                </button>
              ))}
          </div>
        )}

        {role === "mawaqeet" && (
          <div className={`${styles.ctx} ${styles.ctxMawaqeet}`}>
            <span className={styles.ctxRow}>
              <span className={styles.ctxLogo}>
                <img src={mawaqeetLogo} alt="" />
              </span>
              <span className={styles.ctxText}>
                <span className={styles.ctxName}>Mawaqeet</span>
                <span className={styles.ctxSub}>Bønnetidstabell #2</span>
              </span>
            </span>
          </div>
        )}

        <div
          className={`${styles.groups} ${
            role === "superadmin" ? styles.groupsDense : ""
          }`}
        >
          {groups.map((group) => (
            <div key={group.title} className={styles.group}>
              <span className={styles.groupTitle}>{group.title}</span>
              {group.items.map((item) => {
                const active = isItemActive(item, location.pathname);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    aria-current={active ? "page" : undefined}
                    className={`${styles.item} ${active ? styles.itemOn : ""}`}
                  >
                    <Icon name={item.icon} size={18} />
                    {item.label}
                    {countFor(inquirySummary, item.count) > 0 && (
                      <span
                        className={styles.count}
                        aria-label={`${countFor(inquirySummary, item.count)} nye`}
                      >
                        {countFor(inquirySummary, item.count)}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          ))}
        </div>

        <div className={styles.footer}>
          {role === "super-moske" && (
            <button
              type="button"
              className={`${styles.footBtn} ${styles.footGold}`}
              onClick={handleGoToSuperAdmin}
            >
              <Icon name="star" size={18} />
              Til superadmin
            </button>
          )}
          {role === "superadmin" && (
            <>
              <button
                type="button"
                className={`${styles.footBtn} ${styles.footPlain}`}
                onClick={handleCopyToken}
              >
                <Icon name="key" size={17} />
                Kopier token
              </button>
              <button
                type="button"
                className={`${styles.footBtn} ${styles.footMint}`}
                onClick={handleManageMosque}
              >
                <Icon name="mosque" size={18} />
                Administrer en moské
              </button>
            </>
          )}
          <span className={styles.user}>
            <span className={styles.avatar} aria-hidden="true">
              {(email?.[0] ?? (role === "mawaqeet" ? "M" : "A")).toUpperCase()}
            </span>
            <span className={styles.email} title={email ?? ""}>
              {email ?? "Innlogget"}
            </span>
            <button
              type="button"
              className={styles.logout}
              onClick={handleLogout}
              aria-label="Logg ut"
              title="Logg ut"
            >
              <Icon name="logout" size={18} />
            </button>
          </span>
        </div>
      </nav>

      <main className={styles.main}>
        {role === "super-moske" && (
          <div className={ui.banner}>
            <Icon name="star" size={16} strokeWidth={2} />
            <span>
              Du administrerer <b>{displayName || "en moské"}</b> som
              superadmin.
            </span>
            <span className={ui.bannerActs}>
              <button
                type="button"
                className={ui.linkBtn}
                onClick={() => setSwitcherOpen(true)}
              >
                Bytt moské
              </button>
              <button
                type="button"
                className={ui.linkBtn}
                onClick={handleGoToSuperAdmin}
              >
                Tilbake til superadmin
              </button>
            </span>
          </div>
        )}
        <div className={ui.page}>
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
